import { Link } from 'react-router-dom'
import { usePageMeta } from '../hooks/usePageMeta'

export default function TermsPage() {
  usePageMeta(
    'Terms of Service',
    'Terms of service for Biosecurity Reporter, a crowdsourced situational-awareness platform for H5N1 avian influenza sightings in Australia.',
    '/terms',
  )

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>

        <div className="mt-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Terms of Service</h1>
          <p className="mt-1 text-sm text-gray-500">Last updated 15 August 2026</p>
        </div>

        <div className="rounded-xl border-2 border-red-300 bg-red-50 px-5 py-4 mb-8">
          <p className="text-sm font-semibold text-red-800 mb-1">
            This platform is situational awareness, not a biosecurity notification.
          </p>
          <p className="text-sm text-red-700">
            If you suspect an emergency animal disease, call the Emergency Animal Disease Watch
            Hotline on <span className="font-semibold">1800 675 888</span> (free, 24/7) — every
            time, regardless of what you report here. Nothing submitted on this platform is sent
            to DAFF or any state agency.
          </p>
        </div>

        <div className="text-sm text-gray-700 space-y-8">
          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">1. Acceptance of these terms</h2>
            <p>
              By creating an account or using Biosecurity Reporter (&quot;the platform&quot;, &quot;we&quot;,
              &quot;us&quot;), you agree to these Terms of Service and our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">Privacy Policy</Link>.
              If you don&apos;t agree, please don&apos;t use the platform. You must be at least 18
              years old, or have a parent or guardian&apos;s permission, to create an account.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">2. What the platform is — and isn&apos;t</h2>
            <p className="mb-3">
              Biosecurity Reporter is a crowdsourced situational-awareness tool for the H5N1 avian
              influenza outbreak in Australia. Registered users can report potential sightings;
              a background process clusters and corroborates independent reports over time,
              location, and species into a confidence-scored picture.
            </p>
            <p>
              It is <strong>not</strong> an official biosecurity reporting channel, is not
              operated by or affiliated with the Department of Agriculture, Fisheries and Forestry
              (DAFF) or any state biosecurity agency, and no report submitted here constitutes a
              formal notification under the <em>Biosecurity Act 2015</em> (Cth) or any state
              equivalent. A report&apos;s displayed confidence level (unconfirmed, corroborated,
              high confidence, or officially declared) reflects how many independent reports agree
              — it is not a verified diagnosis, and low-confidence reports may turn out to be
              wrong.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">3. Your account</h2>
            <p>
              You&apos;re responsible for keeping your login credentials confidential and for all
              activity under your account. Provide accurate registration information — your
              credibility tier and any professional-verification request are assessed against it.
              One account per person; don&apos;t create accounts to evade a suspension or to
              inflate a sighting&apos;s apparent corroboration.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">4. Submitting reports</h2>
            <p className="mb-3">
              When you submit a report, you&apos;re representing that you&apos;re describing
              something you genuinely observed, to the best of your knowledge, at the location and
              time stated. Reports are published immediately at &quot;unconfirmed&quot; confidence
              without prior review — we don&apos;t verify a report before it becomes visible to
              other users.
            </p>
            <p className="mb-3">
              Don&apos;t submit reports you know to be false, submit reports on behalf of a
              location or property without a genuine basis for doing so, or use the platform to
              harass, target, or make claims about a specific identifiable person, business, or
              property. We may flag, reject, down-weight, or remove any report, and suspend or
              terminate accounts, at our discretion — including in response to other users
              flagging a report as suspicious.
            </p>
            <p>
              You retain ownership of what you submit, but grant us a licence to store, display,
              aggregate, and analyse it as part of operating the platform — including showing
              aggregate (never individually-identified) report counts publicly on the map.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">5. Acceptable use</h2>
            <p>
              Don&apos;t attempt to circumvent rate limits or moderation, scrape or bulk-extract
              data beyond normal use, attempt to identify individual reporters from aggregate data,
              interfere with the platform&apos;s operation, or use it for any unlawful purpose.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">6. No warranty</h2>
            <p>
              The platform is provided &quot;as is&quot;, without warranty of any kind. We don&apos;t
              guarantee that the platform will be available, error-free, or that any report or
              confidence level is accurate or complete. Fusion confidence reflects agreement between
              independent reports, not verified fact.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">7. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, we exclude all liability for any loss or
              damage arising from your use of, or reliance on, the platform — including decisions
              made or not made based on a report or confidence level shown. Nothing in these terms
              excludes, restricts, or modifies any consumer guarantee, right, or remedy under the
              Australian Consumer Law that cannot lawfully be excluded.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">8. Changes</h2>
            <p>
              We may update these terms or the platform itself from time to time. Continued use
              after an update means you accept the revised terms. Material changes will be
              reflected in the &quot;last updated&quot; date above.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">9. Governing law</h2>
            <p>These terms are governed by the laws of Australia.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 mb-2">10. Contact</h2>
            <p>
              Questions about these terms:{' '}
              <a href="mailto:robrussell.development@gmail.com" className="text-blue-600 hover:text-blue-700 font-medium">
                robrussell.development@gmail.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-10">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            ← Back to map
          </Link>
        </div>
      </div>
    </div>
  )
}
